import { useEffect, useState } from "react";
import { useSim } from "../../state/store";
import { audio } from "../../lib/audio";
import { dd, missionStatus, MISSION_ORDER } from "./state";
import { DD_ENTRY, DD_MISSIONS, DD_MISSION_TAGS, DD_ROUTES } from "../../scenario/the-1700-release.content";

/**
 * Course map for "The 17:00 Release": a persistent button (top left) that opens
 * a panel to see what is done, corrected, kept or still open, change the route
 * (full / version A / version B / custom) and jump to any chapter. Rendered by
 * App only for this course; it lives outside the monitor glass so it works on
 * camera beats too.
 */
const STATUS_LABEL: Record<string, string> = {
  done: "Done",
  corrected: "Corrected",
  kept: "Reviewed, kept result",
  started: "Started",
  todo: "Not started",
};
const STATUS_ICON: Record<string, string> = { done: "\u2713", corrected: "\u21BB", kept: "\u2691", started: "\u25D0", todo: "\u25CB" };

function missionOfBeat(id: string | null): string {
  if (!id) return "";
  if (id.includes("replay") || id.includes("cam_out")) return "replay";
  if (id.includes("brief") || id.includes("film") || id.includes("cam_desk")) return "brief";
  const m = /^b_dd_(?:cam_)?(d\d+)/.exec(id);
  return m ? m[1] : "";
}

export function DevDayMap() {
  const beatId = useSim((s) => s.currentBeatId);
  const phase = useSim((s) => s.phase);
  const scenario = useSim((s) => s.scenario);
  const [open, setOpen] = useState(false);
  const [, setTick] = useState(0);
  useEffect(() => {
    const show = () => { setOpen(true); setTick((x) => x + 1); };
    const refresh = () => setTick((x) => x + 1);
    window.addEventListener("dd-open-map", show);
    window.addEventListener("dd-state", refresh);
    return () => {
      window.removeEventListener("dd-open-map", show);
      window.removeEventListener("dd-state", refresh);
    };
  }, []);
  useEffect(() => { setTick((x) => x + 1); }, [beatId]);
  const beat = scenario?.beats.find((b) => b.id === beatId);
  if (phase !== "running" || !beat || beat.type === "intro" || beat.type === "quiz" || beat.type === "debrief") return null;

  const route = dd.route();
  const routeId = dd.get().route?.id ?? "full";
  const here = missionOfBeat(beatId);
  const doneCount = route.filter((m) => ["done", "corrected", "kept"].includes(missionStatus(m))).length;

  const go = (target: string) => {
    if (MISSION_ORDER.includes(target) && !route.includes(target)) dd.addToRoute(target);
    audio.click();
    setOpen(false);
    useSim.getState().goToBeat(DD_ENTRY[target]);
  };
  const toggle = (m: string) => {
    const next = route.includes(m) ? route.filter((x) => x !== m) : [...route, m];
    if (!next.length) return;
    audio.click();
    dd.setRoute("custom", next);
  };
  const pickRoute = (id: string) => {
    const r = DD_ROUTES.find((x) => x.id === id);
    if (!r) return;
    audio.click();
    dd.setRoute(id, r.missions);
  };

  return (
    <>
      <button type="button" className={`dd-mapbtn${open ? " is-open" : ""}`} onClick={() => { audio.click(); setOpen((v) => !v); }} aria-expanded={open}>
        <span aria-hidden="true">{"\u2630"}</span> Course map <small>{doneCount} of {route.length} done</small>
      </button>
      {open && (
        <div className="dd-map" role="dialog" aria-label="Course map">
          <div className="dd-map-h">
            <b>Course map</b>
            <button type="button" className="dd-map-x" onClick={() => setOpen(false)} aria-label="Close">{"\u2715"}</button>
          </div>
          <span className="dd-ih">Your route</span>
          <div className="dd-map-routes">
            {DD_ROUTES.map((r) => (
              <button key={r.id} type="button" className={routeId === r.id ? "is-on" : ""} onClick={() => pickRoute(r.id)} title={r.detail}>
                {r.id === "full" ? "Full" : r.id === "a" ? "Version A" : "Version B"}
                <small>{r.minutes}</small>
              </button>
            ))}
            <span className={`dd-map-custom${routeId === "custom" ? " is-on" : ""}`}>Custom<small>tick below</small></span>
          </div>
          <div className="dd-map-list">
            <div className={`dd-map-row is-fixed${here === "brief" ? " is-here" : ""}`}>
              <span className="dd-map-ic" aria-hidden="true">{"\u25B8"}</span>
              <div className="dd-map-t"><b>Briefing and video</b><small>08:45 {"\u00b7"} the assignment and the 8-minute film</small></div>
              <button type="button" className="dd-map-go" onClick={() => go("brief")}>{here === "brief" ? "Here" : "Go"}</button>
            </div>
            {DD_MISSIONS.map((m, i) => {
              const st = missionStatus(m.id);
              const inRoute = route.includes(m.id);
              return (
                <div key={m.id} className={`dd-map-row is-${st}${inRoute ? "" : " is-out"}${here === m.id ? " is-here" : ""}`}>
                  <button type="button" role="checkbox" aria-checked={inRoute} className="dd-map-check" onClick={() => toggle(m.id)} title={inRoute ? "Remove from my route" : "Add to my route"}>
                    {inRoute ? "\u2713" : ""}
                  </button>
                  <div className="dd-map-t">
                    <b>{i + 1}. {m.title}</b>
                    <small>
                      {m.time} {"\u00b7"} <span className={`dd-map-st is-${st}`}>{STATUS_ICON[st]} {inRoute ? STATUS_LABEL[st] : st === "todo" ? "Not in your route" : STATUS_LABEL[st]}</span>
                    </small>
                    <span className="dd-map-tags">{(DD_MISSION_TAGS[m.id] ?? []).map((t) => <i key={t}>{t}</i>)}</span>
                  </div>
                  <button type="button" className="dd-map-go" onClick={() => go(m.id)}>{here === m.id ? "Here" : st === "todo" ? "Go" : "Revisit"}</button>
                </div>
              );
            })}
            <div className={`dd-map-row is-fixed${here === "replay" ? " is-here" : ""}`}>
              <span className="dd-map-ic" aria-hidden="true">{"\u2691"}</span>
              <div className="dd-map-t"><b>17:00 {"\u00b7"} Your workday, replayed</b><small>Your results, takeaways and the final check</small></div>
              <button type="button" className="dd-map-go" onClick={() => go("replay")}>{here === "replay" ? "Here" : "Finish"}</button>
            </div>
          </div>
          <small className="dd-muted dd-map-foot">Tick or untick missions to build your own route. Missions outside your route are not scored. Your first attempt is kept when you revisit a mission.</small>
        </div>
      )}
    </>
  );
}
